/** Behavioural biometrics tuning constants. All time values in ms, distances in CSS pixels. */

/** Max gap between consecutive pointer samples before we treat the next one as a fresh segment (used to skip idle time). */
export const POINTER_IDLE_GAP_MS = 200;

/** Throttle gate for pointer sampling. Native devices can fire up to 1 kHz; we cap our work to ~60 Hz. */
export const POINTER_SAMPLE_MIN_DT_MS = 16;

/** Minimum displacement (px) for a pointer sample to be counted; below this we treat as jitter. */
export const POINTER_MIN_STEP_PX = 1;

/** Ring buffer size for trajectory analysis (samples per segment cap). */
export const TRAJECTORY_RING_SIZE = 128;

/** Straightness threshold: displacement / path_length above this = "direct" trajectory. */
export const TRAJECTORY_STRAIGHTNESS_DIRECT = 0.85;

/** Cumulative absolute turning angle (degrees) to classify a segment as "circling". */
export const TRAJECTORY_TURN_CIRCLING_DEG = 360;

/** Single direction reversal angle (degrees) inside a segment to classify "backtracking". */
export const TRAJECTORY_REVERSAL_BACKTRACK_DEG = 90;

/** A focus-session keystroke gap longer than this is counted as a "long pause". */
export const KEYSTROKE_LONG_PAUSE_MS = 800;

/** Pause-between-fields threshold (used by serializer; backend can re-aggregate). */
export const INTER_FIELD_PAUSE_MS = 1500;

/** Section is "reading" if visible AND activity occurred within this window. */
export const READING_ACTIVITY_GAP_MS = 2000;

/** Section "pause" segment threshold: no activity in section for at least this long while visible. */
export const SECTION_PAUSE_MS = 1500;

/** Debounce window for selectionchange events. */
export const SELECTION_DEBOUNCE_MS = 300;

/** Lifetime visit counters (localStorage): re-entering a form/page within this window does not bump the counter. */
export const VISIT_DEBOUNCE_MS = 30_000;

/** Focus-by-click detection: max gap between pointerdown and matching focusin. */
export const FOCUS_BY_CLICK_MAX_DT_MS = 200;

/** Focus-by-tab detection: max gap between Tab keydown and matching focusin. */
export const FOCUS_BY_TAB_MAX_DT_MS = 200;

/** IntersectionObserver threshold for "section entered viewport". */
export const SECTION_VISIBLE_RATIO = 0.5;

/** Per-section visits[] array cap. */
export const SECTION_VISITS_CAP = 50;

/** Delay for the appearance: menulist-button autofill probe after focus / first input. */
export const AUTOFILL_PROBE_DELAY_MS = 600;

/** Autofill heuristic: minimum value-length jump in a single input event with no prior keystroke. */
export const AUTOFILL_VALUE_JUMP_MIN = 2;

/** Maximum gap between a confirmed source-field autofill and Safari's sibling date-field updates. */
export const AUTOFILL_BATCH_WINDOW_MS = 250;

/** Grace window for a component's delayed native range event after a gesture fallback was committed. */
export const RANGE_GESTURE_DUPLICATE_GRACE_MS = 500;

/** Pointer-event hover→click latency cap to discard outliers (long hovers across sessions). */
export const HOVER_LATENCY_MAX_MS = 30_000;

/** File-picker selection latency cap to discard stale activation timestamps. */
export const FILE_SELECTION_LATENCY_MAX_MS = 10 * 60_000;

/** Storage keys. */
// Autocomplete state changed: native date-picker events must not be restored
// under the old autocomplete contract.
export const SESSION_STORAGE_KEY = 'rs_bb_state_v2';
export const LOCAL_STORAGE_KEY = 'rs_bb_lifetime_v1';

/** SDK-injected CSS animation name for :-webkit-autofill detection. */
export const AUTOFILL_ANIMATION_NAME = 'rs-bb-autofill';

/** data-rs-* attribute names. */
export const BB_ATTRIBUTE = {
  FORM: 'data-rs-form',
  FIELD: 'data-rs-field',
  SECTION: 'data-rs-section',
  FIELD_CATEGORY: 'data-rs-field-category',
  SECTION_CATEGORY: 'data-rs-section-category',
};

export const ATTR_FORM = BB_ATTRIBUTE.FORM;
export const ATTR_FIELD = BB_ATTRIBUTE.FIELD;
export const ATTR_SECTION = BB_ATTRIBUTE.SECTION;

export const BB_FIELD_CATEGORY = {
  PERSONAL: 'personal',
  SENSITIVE: 'sensitive',
  AMOUNT: 'amount',
};

export const BB_SECTION_CATEGORY = {
  TERMS: 'terms',
  SCHEDULE: 'schedule',
  FAQ: 'faq',
};

export const BB_ATTRIBUTES_MODE = {
  MERGE: 'merge',
  REPLACE: 'replace',
};

/** Interactive selectors used for pointer accuracy "is this an interactive target" classification. */
export const INTERACTIVE_SELECTOR =
  'button, a[href], input, select, textarea, [role="button"], [role="link"], [tabindex]:not([tabindex="-1"])';

/** State machine values persisted in sessionStorage. */
export const LIFECYCLE_IDLE = 'idle';
export const LIFECYCLE_RUNNING = 'running';
export const LIFECYCLE_PAUSED = 'paused';
